import { useNotify } from '@globaleris/ui.library'
import { Button, Paper } from '@material-ui/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity } from '../../../api'
import { EnumTipoLead } from '../../../api/oData/enums/enumTipoLead'
import Page from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { LeadForm, LeadFormModel } from '../components/leadForm/leadForm'
import { AnnexedParamSectionTableModel } from '../../../components/shared/annexedParamAccordionSection/annexedParamAccordionSection.model'
import { mapAddLeadFormToPostRequest } from '../leads.mapper'
import { checkLeadCodeExists, updateLead } from '../leads.service'
import { NonAuthRoutes } from '../../../model'

interface LeadsEditPageProps {
  location?: {
    state: {
      commodity?: EnumCommodity
      leadType?: EnumTipoLead
      leadData: LeadFormModel
      leadAnnexedParams: AnnexedParamSectionTableModel[]
    }
  }
}

export const LeadsEditPage: React.FC<LeadsEditPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'leads', 'enums'])
  const { info, error } = useNotify()
  const history = useHistory()

  const leadData = props.location?.state.leadData
  const [leadFormData, setLeadFormData] = useState<LeadFormModel>(leadData ?? ({} as LeadFormModel))
  const leadAnnexedParams = props.location?.state.leadAnnexedParams
  const [annexedParams, setAnnexedParams] = useState<AnnexedParamSectionTableModel[]>(
    leadAnnexedParams ?? []
  )
  const [formIsValid, setFormIsValid] = useState<boolean>(true) // validations must be initially true if form is on EditMode

  const commodity = props.location?.state.commodity
  const leadType = props.location?.state.leadType

  if (!commodity || !leadType) {
    error(t('leads:fetchErrors.props'))
    history.goBack()
    return <></>
  }

  // #region Handlers Starts

  const handleFormChange = (formState: LeadFormModel, formIsCompleted: boolean) => {
    setLeadFormData(formState)
    setFormIsValid(formIsCompleted)
  }
  const handleAnnexedParamChange = (annexedParamsDataTable: AnnexedParamSectionTableModel[]) => {
    setAnnexedParams(annexedParamsDataTable)
  }

  const handleGoBack = () => {
    history.goBack()
  }

  const handleSave = async () => {
    const duplicateCheck = await checkLeadCodeExists(
      leadFormData.code,
      commodity,
      leadType,
      leadFormData.id
    )
    if (!duplicateCheck.success) {
      error(t('leads:notifications.editLead.error'))
      return
    }
    if (duplicateCheck.data) {
      error(t('leads:notifications.duplicateCode.error'))
      return
    }
    const request = mapAddLeadFormToPostRequest(leadFormData, annexedParams)
    await updateLead(request, request.id!).then(response => {
      if (response.success) {
        info(t('leads:notifications.editLead.success'))
        history.push(NonAuthRoutes.LeadsFile, { leadId: request.id })
      } else {
        error(t('leads:notifications.editLead.error'))
      }
    })
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('leads:forms.edit.title')}>
        <Button variant="text" color="primary" size="small" onClick={handleGoBack}>
          {t('leads:actions.back')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={!formIsValid}
        >
          {t('leads:actions.save')}
        </Button>
      </PageHeader>
      <PageCenterColumn>
        <Paper elevation={6}>
          <LeadForm
            leadFormData={leadFormData}
            annexedParamsData={annexedParams}
            onFormChange={handleFormChange}
            onAnnexedParamsChange={handleAnnexedParamChange}
            commodity={commodity}
            leadType={leadType}
          />
        </Paper>
      </PageCenterColumn>
    </Page>
  )
}
